/**
 * The kanban's drift warning (design board "Sessions kanban"): a recent chat of yours whose files someone else changed
 * since, so resuming it does not overwrite their work blindly. From the project's changes (`GET /collab/changes`,
 * `{ you, changes, runs }`): the files the chat wrote are those of your Flow chat's agent runs that ended around the
 * chat's last update (the changes API does not name the chat; a run within RUN_SLACK_MS of it is taken as its own),
 * then the changes by anyone else (a person, or another agent) to those files after it.
 */
export const RUN_SLACK_MS = 10 * 60 * 1000;

const time = (iso) => Date.parse(iso || '') || 0;
const firstName = (name) => String(name || 'Someone').split(/[\s.@]/)[0];

/** `{ files, by: [names], count }` or null. `session.lastUpdateTime` is in seconds (ADK). */
export function driftFor(session, answer) {
  const youId = answer?.you?.id;
  const last = Number(session?.lastUpdateTime) * 1000;
  if (!youId || !Number.isFinite(last) || last <= 0) { return null; }
  const wrote = new Set();
  (answer?.runs || []).forEach((run) => {
    if (run.author?.id !== youId || run.via !== 'Flow') { return; }
    if (Math.abs(time(run.to) - last) <= RUN_SLACK_MS || (time(run.from) <= last && time(run.to) >= last - RUN_SLACK_MS)) {
      (run.files || []).forEach((path) => wrote.add(path));
    }
  });
  if (wrote.size === 0) { return null; }
  const changed = new Set();
  const by = new Set();
  (answer?.changes || []).forEach((change) => {
    if (time(change.timestamp) <= last + RUN_SLACK_MS / 10) { return; }
    const mineAgain = change.author?.id === youId && (change.via === 'Flow' || !change.via);
    if (mineAgain) { return; }
    const touched = (change.files || []).filter((file) => wrote.has(file.path));
    if (touched.length === 0) { return; }
    touched.forEach((file) => changed.add(file.path));
    by.add(change.via ? `${change.via === 'Flow' ? 'Flow chat' : change.via} for ${change.author?.id === youId ? 'you' : firstName(change.author?.name)}` : firstName(change.author?.name));
  });
  if (changed.size === 0) { return null; }
  return { files: [...changed], by: [...by], count: changed.size };
}

/** "Luca changed 2 files this chat wrote, since" / "Luca and Claude Code for you changed 1 file …". */
export function driftText(drift) {
  if (!drift) { return ''; }
  const who = drift.by.length <= 2 ? drift.by.join(' and ') : `${drift.by.slice(0, 2).join(', ')} and others`;
  return `${who} changed ${drift.count} file${drift.count === 1 ? '' : 's'} this chat wrote, since`;
}
